import { Box, Container } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import NavBar from "./components/NavBar";
import dayjs from "dayjs";
import Header from "./components/Header";
import useLoader from "src/utility/useLoader";
import { CommonApi } from "src/api/CommonApi";
import Loadable from "src/layouts/full/shared/loadable/Loadable";
import { ReceiptApi } from "src/api/ReceiptApi";
import TableForm from "./components/TableForm";
import MasterTable from "./components/MasterTable";
import CenterLoader from "src/utility/CenterLoader";
import { useSnackBar } from "src/context/SnackBarContext";

const initialHeader = {
  rec_no: "",
  staff: null,
  branch: null,
  date: dayjs().format("YYYY-MM-DD"),
  cash: null,
  cheque_number: "",
  note: "",
  narration: "",
  bill_no: "",
};
const initialTable = [
  {
    account_id: null,
    account_name: null,
    division: null,
    amount: "",
    under: "",
    balance: "",
    vat: "",
    vatAmt: "",
  },
];
function Reciept() {
  const { showSnackbar } = useSnackBar();
  const { loading, start, stop } = useLoader();
  const location = useLocation();
  const initialType = location.state?.type || "RECEIPT";
  const [type, setType] = useState(initialType);
  const [bank, setBank] = useState([]);
  const [company, setCompany] = useState(null);
  const [header, setHeader] = useState(initialHeader);
  const [table, setTable] = useState(initialTable);
  const [openMaster, setOpenMaster] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    fetchBanks();
    companyDetails();
    // Fetch latest receipt on load
    fetchRecieptNo(initialType).then((recNo) => {
      if (recNo) fetchReceipt(recNo);
    });
  }, [initialType]);

  const handleChange = async (event, newType) => {
    if (!newType) return;
    setType(newType);
    resetState();
    const recNo = await fetchRecieptNo(newType);
    if (recNo) fetchReceipt(recNo);
  };

  const handleOpenMaster = () => {
    setOpenMaster(true);
  };
  const handleCloseMaster = () => {
    setOpenMaster(false);
  };

  const resetState = () => {
    setHeader({
      ...initialHeader,
      rec_no: "",
      date: dayjs().format("YYYY-MM-DD"),
      bill_no: "",
    });

    setTable([
      {
        account_id: null,
        account_name: null,
        division: null,
        amount: "",
        balance: "",
        vat: "",
        vatAmt: "",
      },
    ]);
  };

  const companyDetails = async () => {
    start();
    try {
      const res = await CommonApi.getCompanyDetails();
      setCompany(res?.[0]);
    } catch (error) {
      console.log(error);
    } finally {
      stop();
    }
  };

  const fetchBanks = async () => {
    start();
    try {
      const res = await CommonApi.getBanks();
      if (res?.success) {
        setBank(res?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      stop();
    }
  };

  const fetchRecieptNo = async (params) => {
    start();
    try {
      const res = await ReceiptApi.getReceiptNo({ type: params || type });
      if (res?.success) {
        setHeader((prev) => ({ ...prev, rec_no: res?.rec_no }));
        return res?.rec_no;
      }
    } catch (error) {
      console.log(error);
    } finally {
      stop();
    }
    return null;
  };

  const fetchReceipt = async (params) => {
    start();
    try {
      const res = await ReceiptApi.getReceiptPaymentById({
        rec_no: params,
      });

      const data = res?.data || [];

      if (!data || data.length === 0) {
        resetState();
        setHeader((prev) => ({ ...prev, rec_no: params }));
        return;
      }

      // Map Collection rows to header + table
      const firstRow = data[0];
      setHeader((prev) => ({
        ...prev,
        rec_no: firstRow.SiNo || params,
        date: firstRow.Date1
          ? dayjs(firstRow.Date1).format("YYYY-MM-DD")
          : prev.date,
        narration: firstRow.Narration || "",
        note: firstRow.Narration || "",
        bill_no: firstRow.BillNo || "",
      }));

      // Filter out ledger rows (CASH ACCOUNT / BANK ACCOUNT) — only show customer/party rows
      const customerRows = data.filter((item) => item.Typ !== "CASH ACCOUNT" && item.Typ !== "BANK ACCOUNT");

      const formattedTable = customerRows.map((item) => ({
        sino: item.SiNo,
        account_id: null,
        account_name: item.PartyName || "",
        division: null,
        amount: item.Amount || item.NetTotal || "",
        under: item.Typ || "",
        balance: "",
        vat: item.TaxP || "",
        vatAmt: item.TaxAmt || "",
        transactionType: item.TransactionType || "",
      }));
      setTable(formattedTable.length > 0 ? formattedTable : initialTable);
    } catch (error) {
      console.log(error);
      resetState();
    } finally {
      stop();
    }
  };

  const addReceipt = async () => {
    start();
    const payload = { header, table, type };
    try {
      const res = await ReceiptApi.addReceiptPayment(payload);
      if (res?.success) {
        showSnackbar("success", "Saved successfully");
        fetchReceipt(header.rec_no);
      } else {
        showSnackbar("error", res?.message || "Failed to save");
      }
    } catch (error) {
      console.log(error);
      showSnackbar("error", "An error occurred while saving");
    } finally {
      stop();
    }
  };

  return (
    <div>
      <CenterLoader open={loading} />
      <MasterTable
        openMaster={openMaster}
        handleCloseMaster={handleCloseMaster}
        setTable={setTable}
        selectedRow={selectedRow}
      />
      <Box height={"100vh"}>
        <NavBar type={type} handleChange={handleChange} />
        <Container>
          <Header
            header={header}
            setHeader={setHeader}
            bank={bank}
            fetchReceipt={fetchReceipt}
            readOnly={true}
          />
          <TableForm
            key={type}
            setTable={setTable}
            table={table}
            handleOpenMaster={handleOpenMaster}
            setSelectedRow={setSelectedRow}
            header={header}
            setHeader={setHeader}
            fetchReceipt={fetchReceipt}
            fetchRecieptNo={fetchRecieptNo}
            addReceipt={addReceipt}
            readOnly={true}
          />
        </Container>
      </Box>
    </div>
  );
}

export default Reciept;
